/**
 * M24 task-09 — new DB connector form (DBC-01/02; HUB-079 §4). Hand-rolled
 * form with a direct hub call (relations/Create precedent). The connection
 * string is a WRITE-ONLY password field — it is sent once on create and
 * never rendered from any response (responses don't carry it). Creation
 * queues LLM schema generation server-side, so success navigates straight
 * to Show where the generating status is visible.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { DbConnectorEngine, DbConnectorItem } from '@verybigthings/trackframe-shared/schemas';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';

export const BI_PROMPT_LABEL = 'What matters from a BI perspective? Dimensions and facts…';

const ENGINES: DbConnectorEngine[] = ['postgresql', 'mssql'];

export function DbConnectorsCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [engine, setEngine] = useState<DbConnectorEngine>('postgresql');
  const [connectionString, setConnectionString] = useState('');
  const [biPrompt, setBiPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const row: DbConnectorItem = await hub.dbConnectorsCreate({
        body: {
          name: name.trim(),
          engine,
          connectionString,
          ...(biPrompt.trim() ? { biPrompt: biPrompt.trim() } : {}),
        },
      });
      navigate(`/admin/db-connectors/${row.id}`);
    } catch (err) {
      if (err instanceof HubClientError && err.code === 'name_taken') {
        setError(`A connector named "${name.trim()}" already exists.`);
      } else {
        setError((err as Error).message);
      }
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New DB connector</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connection</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="warehouse"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Engine</Label>
              <div className="flex gap-4 text-sm">
                {ENGINES.map((value) => (
                  <label key={value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="engine"
                      value={value}
                      checked={engine === value}
                      onChange={() => setEngine(value)}
                    />
                    {value}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="connectionString">Connection string</Label>
              <Input
                id="connectionString"
                type="password"
                autoComplete="off"
                placeholder="postgresql://user:pass@host:5432/db"
                value={connectionString}
                onChange={(e) => setConnectionString(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Stored encrypted; never shown again. <strong>Required:</strong> a read-only
                database user. Connectors never write, and the hub refuses every non-SELECT
                statement — but only the credential bounds the database itself. See
                “Database connectors: read-only credentials” in DEPLOY.md.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="biPrompt">{BI_PROMPT_LABEL}</Label>
              <textarea
                id="biPrompt"
                rows={4}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="e.g. Revenue by customer segment and month; churn; order fulfilment lead time…"
                value={biPrompt}
                onChange={(e) => setBiPrompt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Feeds the LLM schema generation that is queued right after create.
              </p>
            </div>
            {error && <Alert>{error}</Alert>}
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? 'Creating…' : 'Create connector'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/db-connectors')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
